import React, { useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs } from "../../../../../../types/walletTypes";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { useBankList } from "../../../../../../app/hooks/useBankList";
import { CHINESE_NATIONAL_ID_RULES } from "../../../shared/helpers";
import {
  ContentWrapper,
  InputWrapper,
  Error,
  NotificationWrapper,
  ButtonsWrapper,
} from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { ReactComponent as InfoIcon } from "../../../../../../assets/icons/info.svg";

interface ChinaBeneficiaryFormProps {
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const ChinaBeneficiaryForm: React.FC<ChinaBeneficiaryFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData = useAppSelector((state) => (state.formData as any)?.asiaPayoutData?.cn || {});
  const { businessId } = useAppSelector((state) => state.users);
  const { loading } = useAppSelector((state) => state?.wallets || {});

  const { banks, selectBanks, loading: banksLoading } = useBankList({
    country: "CNY",
    businessId,
  });

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: asiaPayoutData,
    mode: "onChange"
  });

  useEffect(() => {
    if (asiaPayoutData && Object.keys(asiaPayoutData).length > 0) {
      reset({ ...asiaPayoutData });
    }
  }, [asiaPayoutData, reset]);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const updatedData: FormInputs = {
      ...asiaPayoutData,
      ...data,
    };
    dispatch(savedAsiaPayoutData({ country: "cn", data: updatedData }));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading || banksLoading} />

      <InputWrapper>
        <Controller
          name="chineseFullName"
          control={control}
          rules={{
            required: "Full name is required",
            pattern: {
              value: /^[\u4e00-\u9fa5\s]+$/,
              message: "Please enter a valid Chinese name",
            },
          }}
          render={({ field }) => (
            <Input
              type="text"
              label="Full name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.chineseFullName}
            />
          )}
        />
        <NotificationWrapper>
          <InfoIcon />
          <p>The name must be in Chinese</p>
        </NotificationWrapper>
        {errors?.chineseFullName?.message && (
          <Error>{String(errors.chineseFullName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverIdNumber"
          control={control}
          rules={CHINESE_NATIONAL_ID_RULES()}
          render={({ field }) => (
            <Input
              type="text"
              label="Chinese National ID"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.receiverIdNumber}
            />
          )}
        />
        <NotificationWrapper>
          <InfoIcon />
          <p>NIN for Individual or USCC for Business</p>
        </NotificationWrapper>
        {errors?.receiverIdNumber?.message && (
          <Error>{String(errors.receiverIdNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="bankCode"
          control={control}
          rules={{ required: "Bank name is required" }}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={selectBanks.map((b) => ({ label: b.label, value: b.originalCode }))}
              onChange={(selectedValue) => {
                field.onChange(selectedValue);
                const selectedBank = banks?.find((b) => b.code === selectedValue);
                setValue("bankName", selectedBank?.name || "");
              }}
              $width="100%"
              placeholder={banksLoading ? "Loading banks..." : "Bank name"}
              showSearch
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              }
              lightBorder
            />
          )}
        />
        {errors?.bankCode?.message && (
          <Error>{String(errors.bankCode.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountNumber"
          control={control}
          rules={{ required: "Account number is required" }}
          render={({ field }) => (
            <Input
              type="number"
              label="Account number"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.accountNumber}
            />
          )}
        />
        {errors?.accountNumber?.message && (
          <Error>{String(errors.accountNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="swiftCode"
          control={control}
          rules={{ required: "SWIFT code is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="SWIFT code"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.swiftCode}
            />
          )}
        />
        {errors?.swiftCode?.message && (
          <Error>{String(errors.swiftCode.message)}</Error>
        )}
      </InputWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={!isValid} label="Continue" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default ChinaBeneficiaryForm;
